import { getImageFilename } from "@services/dynamicPageService";

// Allow only safe URL schemes — blocks javascript:, data:, vbscript:, etc.
const SAFE_URL_RE = /^(https?:\/\/|\/)/i;

/**
 * Sanitizes a URL from DP content. Returns the URL if its scheme is safe
 * (http, https, or root-relative), otherwise returns an empty string.
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  return SAFE_URL_RE.test(url.trim()) ? url : "";
};

/**
 * Builds the base CDN URL for a dynamic page's content assets.
 * Content is stored under: /static/content/{category}/{name}/{version}/
 * Shared assets (assets/<hash>) are resolved at the page root by resolveImageSrc
 * and getDynamicAssetUrl when the filename carries the assets/ prefix.
 */
export const buildContentUrl = (cloudFrontURL, category, versionName) => {
  const base = String(cloudFrontURL || "").replace(/(?<!\/)\/+$/, "");
  if (!versionName || !versionName.includes("#")) {
    return `${base}/static/content/${category}/${versionName || ""}/`;
  }
  const [name, version] = versionName.split("#");
  return `${base}/static/content/${category}/${name}/${version}/`;
};

/**
 * Parses an inline CSS string (e.g. "color: red; font-size: 14px;") into
 * a React-compatible style object (e.g. { color: "red", fontSize: "14px" }).
 * Malformed pairs are silently skipped.
 */
export function parseInlineCss(cssString) {
  if (!cssString || typeof cssString !== "string") return {};
  const style = {};
  cssString.split(";").forEach((pair) => {
    const colonIdx = pair.indexOf(":");
    if (colonIdx < 1) return;
    const prop = pair.slice(0, colonIdx).trim();
    const value = pair.slice(colonIdx + 1).trim();
    if (!prop || !value) return;
    // Convert kebab-case to camelCase for React
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    style[camel] = value;
  });
  return style;
}

/**
 * Builds a merged custom style object from component options.
 * Picks only the named style keys, then merges any inlineCss on top.
 * Returns undefined if customStyles is not enabled.
 */
export function buildCustomStyle(options, keys) {
  if (!options?.customStyles) return undefined;
  const styles = options.styles || {};
  const picked = {};
  for (const key of keys) {
    if (key in styles) picked[key] = styles[key];
  }
  const inlineCss = styles.inlineCss ? parseInlineCss(styles.inlineCss) : {};
  return { ...picked, ...inlineCss };
}

// ---------------------------------------------------------------------------
// Shared block mapping: certain variant key prefixes always produce a fixed
// BEM block class regardless of the calling component's block. This keeps
// text styling (color, weight, align) in dedicated utility namespaces.
//
// Order matters — longer prefixes must come first to avoid false matches.
// ---------------------------------------------------------------------------
const SHARED_BLOCK_MAP = [
  ["textDecoration", "dp-text-decoration"],
  ["textColor", "dp-text-color"],
  ["mobileAlign", "dp-mobile-text-align"],
  ["align", "dp-text-align"],
  ["weight", "dp-font-weight"],
];

/**
 * Converts a camelCase variation key to a BEM modifier class.
 *
 * Shared-block keys are mapped to their dedicated utility namespace:
 *   toVariationClass("textColorBlack", ...) → "dp-text-color--black"
 *   toVariationClass("weightBold", ...)     → "dp-font-weight--bold"
 *   toVariationClass("alignLeft", ...)      → "dp-text-align--left"
 *   toVariationClass("mobileAlignCenter", ...) → "dp-mobile-text-align--center"
 *
 * All other keys use the provided block:
 *   toVariationClass("sizeLg", "dp-text")   → "dp-text--size-lg"
 *   toVariationClass("gapMd", "dp-card-row") → "dp-card-row--gap-md"
 *
 * Returns empty string for falsy or "__custom__" keys.
 */
export const toVariationClass = (variantKey, block) => {
  if (!variantKey || variantKey === "__custom__") return "";

  for (const [prefix, sharedBlock] of SHARED_BLOCK_MAP) {
    if (variantKey.startsWith(prefix)) {
      const suffix = variantKey.slice(prefix.length);
      const kebab = suffix.replace(/([A-Z])/g, "-$1").toLowerCase();
      return `${sharedBlock}--${kebab.replace(/^-/, "")}`;
    }
  }

  const kebab = variantKey.replace(/([A-Z])/g, "-$1").toLowerCase();
  return `${block || "dp"}--${kebab}`;
};

/**
 * Resolves variant classes from the `variants` array.
 * Returns a space-separated class string.
 *
 * `block` is forwarded to toVariationClass so component-specific keys stay
 * scoped while shared keys always use their dedicated namespace.
 */
export const toVariationClasses = (options, block) => {
  if (!options) return "";
  if (Array.isArray(options.variants)) {
    return options.variants
      .map((v) => toVariationClass(v, block))
      .filter(Boolean)
      .join(" ");
  }
  // Legacy single-string format
  if (options.variant) {
    return toVariationClass(options.variant, block);
  }
  return "";
};

/**
 * Builds a scoped custom CSS class name from a node ID.
 * e.g. "img_abc123" → "dp-custom-img-abc123"
 */
export const customClassName = (nodeId) =>
  `dp-custom-${nodeId.replace(/[^a-zA-Z0-9-]/g, "-")}`;

/**
 * Checks whether custom styles are active for a node's options.
 * Custom is active when the `__custom__` flag is in the variants array.
 */
export const hasCustomStyles = (options) =>
  Array.isArray(options?.variants) && options.variants.includes("__custom__");

/**
 * Sanitizes user-authored CSS for safe scoped injection.
 * - Removes all at-rules (e.g. @import, @font-face, @keyframes)
 * - Drops braces so style text cannot break out of the scoped selector block
 * - Removes comment markers and HTML comment tokens
 */
export const sanitizeCss = (css) => {
  if (!css || typeof css !== "string") {
    return "";
  }

  let clean = css;
  clean = clean.replace(/@[a-z][^;{}]*;/gi, "");
  clean = clean.replace(/@[a-z][^{}]*\{[^}]*\}/gi, "");
  clean = clean.replace(/[{}]/g, "");
  clean = clean.replace(/<!--|-->/g, "");
  clean = clean.replace(/\/\*|\*\//g, "");

  return clean.trim();
};

/**
 * Validates a user-authored CSS length for safe inline injection (e.g. a
 * component "Desktop Width" hatch). Accepts a single positive number + unit
 * (px, ch, rem, em, %, vw, vh). Returns the trimmed value, or "" if it doesn't
 * match — so a bad/hostile value is never injected into the page stylesheet.
 */
export const sanitizeCssLength = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const v = value.trim();
  return /^\d+(\.\d+)?(px|ch|rem|em|%|vw|vh)$/i.test(v) ? v : "";
};

/**
 * Resolves an image src from a child node using base content URL.
 * Shared, content-addressed assets (assets/<hash>) live at the page root, so
 * drop the trailing version segment from baseContentUrl; legacy per-version
 * files resolve directly beneath it.
 */

export const resolveImageSrc = (child, baseContentUrl = "") => {
  const filename = getImageFilename(child?.content);
  if (!filename) {
    return "";
  }
  // Shared assets carry the `assets/` prefix — resolve at page root (drop version segment)
  if (String(filename).startsWith("assets/")) {
    const pageRoot = baseContentUrl.replace(/(?<![^/])[^/]+\/?$/, "");
    return `${pageRoot}${filename}`;
  }
  // Non-shared filenames live in the version folder
  return `${baseContentUrl}${filename}`;
};

/**
 * Resolves a DP content reference (a shared `assets/` ref or an absolute URL) to
 * a full URL against baseContentUrl. This is the canonical assets-aware resolver
 * for image AND pdf refs held as strings:
 *  - absolute URL          → returned unchanged (genuinely external asset)
 *  - `assets/…` shared ref → resolved at the page root (version segment dropped)
 *
 * ES-13428 moved EVERY DP asset into the shared assets/ library. There is NO
 * backward-compatible fallback for a legacy per-version filename or a raw text
 * path: such a ref means the migration missed this content, so it is surfaced as
 * a console error (and resolves to "" → a visibly broken asset) rather than
 * silently building a version-folder URL that would mask the gap.
 */
export const resolveContentRef = (ref, baseContentUrl = "") => {
  // Uploaded PDF/image content is stored as an object { original: { filename } }
  // (the DP uploader shape); accept that alongside the legacy string ref.
  if (ref && typeof ref === "object") {
    ref = ref.original?.filename || ref.filename || "";
  }
  const value = typeof ref === "string" ? ref.trim() : "";
  if (!value) {
    return "";
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  if (value.startsWith("assets/")) {
    const pageRoot = baseContentUrl.replace(/(?<![^/])[^/]+\/?$/, "");
    return `${pageRoot}${value}`;
  }
  console.error(
    `[ES-13428][DP] Un-migrated asset ref (expected "assets/…" or an absolute URL): "${value}"`,
  );
  return "";
};

/**
 * Extracts dynamic page coordinates (category, name, version) from a
 * baseContentUrl of the form: {cdn}/static/content/{category}/{name}/{version}/
 */
export const parseDynamicPageCoords = (baseContentUrl = "") => {
  const match = baseContentUrl.match(
    /\/static\/content\/([^/]+)\/([^/]+)\/([^/]+)\/?$/,
  );
  if (!match) return {};
  return {
    dynamicCategory: match[1],
    dynamicName: match[2],
    dynamicVersion: match[3],
  };
};

/**
 * Resolves button display text. If an explicit label is provided, use it.
 * Otherwise, if the URL points to a PDF, extract the filename without extension.
 * Falls back to "Download" if nothing else is available.
 */
export const resolveButtonLabel = (label, url) => {
  if (label) return label;
  if (!url) return "Download";
  const filename = url.split("/").pop() || "";
  if (filename.toLowerCase().endsWith(".pdf")) {
    return filename.replace(/\.pdf$/i, "");
  }
  return filename || "Download";
};

/**
 * Resolves an AMM card's FAQ list to the shape FaqAccordion consumes:
 * [{ question, answer }, ...].
 *
 * Two storage formats coexist. `faqItems` is the authorable nested group added
 * in ES-13620; `faqsJson` is the legacy hand-written JSON string it replaces.
 * Structured entries win when present so a page picks up the new format as soon
 * as it is backfilled, with no lockstep between the data migration and this
 * deploy — and un-backfilled environments keep rendering from the blob.
 *
 * The Array.isArray guard matters: JSON.parse succeeds on any valid JSON, so a
 * card authored as `{"question":"..."}` instead of `[{...}]` used to survive the
 * try/catch and then throw "faqs.map is not a function" inside InfoCards,
 * taking down the whole dynamic page rather than just one card.
 */
export const resolveFaqItems = (card) => {
  const items = card?.faqItems;
  if (Array.isArray(items) && items.length) {
    return items
      .map((item) => ({
        question: item?.question?.content || "",
        answer: item?.answer?.content || "",
      }))
      .filter((item) => item.question || item.answer);
  }

  try {
    const parsed = JSON.parse(card?.faqsJson?.content || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};
